import { normalizeGameImageUrl } from 'utils/gameImageUrl';

type GameSummaryServerData = {
  game_id?: string;
  upc?: string;
  name?: string;
  start_date?: string;
  end_date?: string;
  rate?: number;
  game_image_url?: string;
  tickets_per_pack?: number;
  activated_count?: {
    packs?: number;
    tickets?: number;
  };
  activated_on_counter?: {
    packs?: number;
    tickets?: number;
  };
  activated_backstock?: {
    packs?: number;
    tickets?: number;
  };
  activated_on_vending?: {
    packs?: number;
    tickets?: number;
  };
  on_vending_value?: number;
  weekly_sales?: number;
  weekly_sold?: number;
  remaining_tickets?: number;
};

export interface GameSummaryData {
  gameId?: string;
  gameImage?: string;
  gameName?: string;
  dateRange?: string;
  gameNumber?: string;
  upc?: string;
  ticketPrice?: number;
  ticketsPerPack?: number;
  activatedPacks?: number;
  activatedTickets?: number;
  onCounter?: number;
  onCounterTickets?: number;
  inBackstock?: number;
  inBackstockTickets?: number;
  activatedOnVending?: number;
  activatedOnVendingTickets?: number;
  onVendingValue?: number;
  weeklySales?: number;
  weeklySalesTickets?: number;
  remainingTickets?: number;
}

class GameSummaryModel {
  private _data: GameSummaryServerData;
  private _hasOnVending: boolean;

  constructor(data: GameSummaryServerData = {}) {
    this._data = data;
    this._hasOnVending = Object.prototype.hasOwnProperty.call(data, 'activated_on_vending')
      && data.activated_on_vending != null;
  }

  get gameId(): string {
    return this._data.game_id ?? '';
  }

  get upc(): string {
    return this._data.upc ?? '';
  }

  get name(): string {
    return this._data.name ?? '';
  }

  get startDate(): string {
    return this._data.start_date ?? '';
  }

  get endDate(): string {
    return this._data.end_date ?? '';
  }

  get rate(): number {
    return Number(this._data.rate ?? 0);
  }

  get ticketsPerPack(): number {
    return Number(this._data.tickets_per_pack ?? 0);
  }

  private _getCountValue(key: keyof GameSummaryServerData): number {
    const inner = this._data[key] as any;
    return Number(inner?.packs ?? 0);
  }

  private _getTicketValue(key: keyof GameSummaryServerData): number {
    const inner = this._data[key] as any;
    return Number(inner?.tickets ?? 0);
  }

  get activatedPacks(): number {
    return this._getCountValue('activated_count');
  }

  get activatedTickets(): number {
    return this._getTicketValue('activated_count');
  }

  get onCounter(): number {
    return this._getCountValue('activated_on_counter');
  }

  get onCounterTickets(): number {
    return this._getTicketValue('activated_on_counter');
  }

  get inBackstock(): number {
    return this._getCountValue('activated_backstock');
  }

  get inBackstockTickets(): number {
    return this._getTicketValue('activated_backstock');
  }

  get activatedOnVending(): number {
    return this._getCountValue('activated_on_vending');
  }

  get activatedOnVendingTickets(): number {
    return this._getTicketValue('activated_on_vending');
  }

  get onVendingValue(): number {
    return Number(this._data.on_vending_value ?? 0);
  }

  get weeklySales(): number {
    return Number(this._data.weekly_sales ?? 0);
  }

  get weeklySalesTickets(): number {
    return Number(this._data.weekly_sold ?? 0);
  }

  get remainingTickets(): number {
    return Number(this._data.remaining_tickets ?? 0);
  }

  get gameImage(): string {
    return normalizeGameImageUrl(this._data.game_image_url);
  }

  private _formatLabelDate(value?: string): string {
    if (!value || value.toLowerCase() === 'na') {
      return 'Present';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get dateRange(): string {
    const start = this._formatLabelDate(this.startDate);
    return start;
  }

  toSummaryData(): GameSummaryData {
    const hasOnVending = this._hasOnVending;
    return {
      gameId: this.gameId,
      gameImage: this.gameImage,
      gameName: this.name,
      dateRange: this.dateRange,
      gameNumber: this.gameId,
      upc: this.upc,
      ticketPrice: this.rate,
      ticketsPerPack: this.ticketsPerPack,
      activatedPacks: this.activatedPacks,
      activatedTickets: this.activatedTickets,
      onCounter: this.onCounter,
      onCounterTickets: this.onCounterTickets,
      inBackstock: this.inBackstock,
      inBackstockTickets: this.inBackstockTickets,
      activatedOnVending: hasOnVending ? this.activatedOnVending : undefined,
      activatedOnVendingTickets: hasOnVending ? this.activatedOnVendingTickets : undefined,
      onVendingValue: hasOnVending ? this.onVendingValue : undefined,
      weeklySales: this.weeklySales,
      weeklySalesTickets: this.weeklySalesTickets,
      remainingTickets: this.remainingTickets,
    };
  }

  static fromServerData(data: GameSummaryServerData | null | undefined) {
    return new GameSummaryModel(data ?? {});
  }

  static createEmpty() {
    return new GameSummaryModel();
  }
}

export default GameSummaryModel;
